<template>
  <div class="download-ctn">
    <v-btn v-if="mode == 'codes' && !hideActivator" @click="open" color="primary" raised :disabled="!count">{{$t('download')}}</v-btn>
    <v-tooltip v-if="mode == 'single'" top>
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="attrs" v-on="on" @click="open" color="primary" icon>
          <v-icon color="#15bbf0">mdi-qrcode</v-icon>
        </v-btn>
      </template>
      <span>{{$t('preview_and_download')}}</span>
    </v-tooltip>
    <v-tooltip v-if="mode == 'product'" top>
      <template v-slot:activator="{ on, attrs }">
        <v-btn :disabled="isDisabled" v-bind="attrs" v-on="on" @click="open" color="primary" icon>
          <v-icon color="#15bbf0">mdi-download</v-icon>
        </v-btn>
      </template>
      <span>{{$t('download')}}</span>
    </v-tooltip>

    <v-dialog width="600" v-if="renameTemplateDialog === true" v-model="renameTemplateDialog">
      <v-card>
        <v-card-title>
          {{$t('qr_rename_template')}}
        </v-card-title>
        <v-card-text class="my-2 pb-0">
          <v-text-field v-model="renameName" :maxlength="200" :placeholder="$t('qr_template_name')" dense outlined ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <div class="mr-auto"></div>
          <v-btn class="mr-2" @click="closeRenameTemplateDialog" text>{{$t('cancel')}}</v-btn>
          <!-- NOTE once a name has been set, backend does not allow it to be changed back to blank -->
          <v-btn class="mr-2" color="primary" @click="renameTemplate" :loading="renamingTemplate" :disabled="!renameName" text>{{$t('save')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="600" v-model="deleteQrDialog">
      <v-card>
        <v-card-title>
          {{$t('delete_confirmation')}}
        </v-card-title>
        <v-card-text class="my-2">
          {{$t('qr_template_delete_confirmation')}}
        </v-card-text>
        <v-card-actions>
          <div class="mr-auto"></div>
          <v-btn class="mr-2" @click="deleteQrDialog = false" text>{{$t('cancel')}}</v-btn>
          <v-btn class="mr-2" color="red" @click="deleteQrTemplate" :loading="deletingTemplate" text>{{$t('delete')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog scrollable v-if="dialog" :width="(mode === 'single' || codes.length === 1) ? 800 : 650" persistent v-model="dialog">
      <v-card class="pb-2">
        <v-card-title>
          <span v-if="mode != 'single'">{{$t('download_format')}}</span>
          <span v-if="mode == 'single'">{{$t('preview_and_download')}}</span>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="close" text>{{$t('cancel')}}</v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-stepper v-model="step">
            <v-stepper-header>
              <v-stepper-step v-if="hasPrefixOptions" step="1">{{$t('select_url_prefix')}}</v-stepper-step>
              <v-divider v-if="hasPrefixOptions"></v-divider>
              <v-stepper-step :step="hasPrefixOptions ? 2 : 1">{{$t('designing_your_qr_code')}}</v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :step="hasPrefixOptions ? 3 : 2">{{$t('file_format')}}</v-stepper-step>
            </v-stepper-header>
            <v-stepper-items>
              <v-stepper-content v-if="hasPrefixOptions" step="1" class="prefixes-ctn">
                <v-row class="ma-0">
                  <v-col>
                    <div class="d-flex flex-column justify-center">
                      <v-select v-model="selectedPrefix" :items="formattedPrefixes" :label="$t('all_url_prefixes')" item-text="displayText" item-value="prefix"
                      @change="onPrefixChange"
                      persistent-hint :hint="hasStoredPrefix ? $t('prefix_used_last_download') : $t('prefix_will_be_associated_with_codes_selected')">
                        <template v-slot:selection="{ item }">
                          <div class="prefix">{{item.displayText}}</div>
                        </template>
                      </v-select>
                      <span class="color-error mt-2" v-if="showPrefixConflictMessage && !hasObsoletePrefix">{{$t('prefix_conflict_message')}}</span>
                      <span class="color-error mt-2" v-if="hasObsoletePrefix && codes.length === 1">{{$t('has_deleted_prefix')}}</span>
                      <span class="color-error mt-2" v-if="hasObsoletePrefix && codes.length > 1">{{$t('have_deleted_prefix')}}</span>
                      <span class="color-error mt-2" v-if="showPrefixRequiredMessage">{{$t('prefix_required')}}</span>
                    </div>
                  </v-col>
                </v-row>
              </v-stepper-content>
              <v-stepper-content class="pb-0" :step="hasPrefixOptions ? 2 : 1">
                <v-row align="start" class="ma-0">
                  <v-col class="pr-0">
                    <div class="scrollable-content">
                      <div v-if="qrTemplates.length">
                        <div class="label font-weight-bold pb-1">{{$t('qr_code_templates')}}</div>
                        <v-select :label="$t('qr_template_select')" v-model="selectedTemplate" :items="qrTemplates" item-value="id" return-object clearable solo class="custom-v-select">
                          <template v-slot:selection="{ item }">
                            <div class="select-qr-preview mr-3">
                              <img class="template-qr-preview" :src="getTemplatePreviewUrl(item)">
                              <img class="template-qr-logo" :style="{backgroundColor: item.background}" v-if="item.image" :src="toDataUrl(item.image)">
                            </div>
                            <v-list-item-content>
                              <span class="preview-item-title">{{ item.name }}</span>
                              <span v-html="sanitizeItemFileName(item.filename)" class="preview-item-subtitle mr-3 max-w-295" :title="item.filename"></span>
                              <span v-html="sanitizeItemColor(`${item.color} - ${item.background}`)" class="preview-item-subtitle"></span>
                            </v-list-item-content>
                          </template>
                          <template v-slot:item="{ item }">
                            <div class="select-qr-preview mr-3">
                              <img class="template-qr-preview" :src="getTemplatePreviewUrl(item)">
                              <img class="template-qr-logo" :style="{backgroundColor: item.background}" v-if="item.image" :src="toDataUrl(item.image)">
                            </div>
                            <v-list-item-content>
                              <span class="preview-item-title">{{ item.name }}</span>
                              <span v-html="sanitizeItemFileName(item.filename)" class="preview-item-subtitle mr-3"></span>
                              <span v-html="sanitizeItemColor(`${item.color} - ${item.background}`)" class="preview-item-subtitle"></span>
                            </v-list-item-content>
                            <v-list-item-action>
                              <v-btn @click="openRenameTemplateDialog(item)" text color="primary">
                                {{$t('rename')}}
                              </v-btn>
                            </v-list-item-action>
                            <v-list-item-action class="ml-1">
                              <v-btn @click="deleteQrTemplateConfirm(item)" text color="red">
                                {{$t('delete')}}
                              </v-btn>
                            </v-list-item-action>
                          </template>
                        </v-select>
                        <div v-if="showTextDownscaledWarning" class="red--text qr-downscale-warning">{{ $t('qr_text_downscaled_warning') }}</div>
                      </div>

                      <v-row>
                        <v-col>
                          <div class="label font-weight-bold pb-1">{{$t('qr_color')}}:</div>
                          <color-input :color.sync="form.color"></color-input>
                        </v-col>
                        <v-col>
                          <div class="label font-weight-bold pb-1">{{$t('background_color')}}:</div>
                          <color-input :color.sync="form.background"></color-input>
                        </v-col>
                      </v-row>

                      <div class="color-error pt-5 d-flex" v-if="hasContrastIssue">
                        <v-icon color="red" class="mr-3">mdi-alert</v-icon>
                        <div class="font-weight-bold" v-html="$t('qr_contrast_warning')"></div>
                      </div>

                      <text-around-qr-code-settings
                      v-if="enableQrTextSettings"
                      ref="text-around-qr-code-settings"
                      :codes="codes"
                      :product="product"
                      :elabel-data="elabelData"
                      :selected-template="selectedTemplate"
                      :text-positions="textPositions"
                      :qr-scale="qrScale"
                      :text-scale="textScale"
                      @text-data-updated="onTextSettingsUpdated"
                      @qr-scale-updated="onQrScaleUpdated"
                      @text-scale-updated="onTextScaleUpdated"
                      ></text-around-qr-code-settings>
                    </div>
                  </v-col>
                  <v-col v-if="mode === 'single' || codes.length === 1" align-self="center" class="flex-grow-0 preview-col pr-0">
                    <div class="label font-weight-bold pb-1 text-center">{{$t('qr_preview')}}</div>
                    <div class="qr-preview d-flex flex-column justify-center">
                      <div class="qr-preview-ctn">
                        <img class="qr" :src="previewUrl">
                        <img v-if="form.image && textSettings?.iconSetting === 'inside'" :src="toDataUrl(form.image)" class="logo" :style="logoStyle"/>
                      </div>
                      <div v-if="hasTextOverflow" class="warn mb-2">
                        {{$t('qr_text_overflow_warning', { count: textOverflowDetails.filter(d => d.overflow).length })}}
                      </div>

                      <div class="mt-auto mb-2"></div>

                      <ImageCropper
                        :ratio="1"
                        :label="$t('upload_logo')"
                        input-key="qr-logo"
                        @image-selected="onImageSelect"
                        :show-select-dialog="false"
                      />
                      <div>
                        <v-btn @click="deleteLogo" class="inset mt-3" text v-if="form.image">{{$t('remove_logo')}}</v-btn>
                        <v-btn @click="addDefaultLogo" class="inset mt-3" v-if="!form.image" text>
                          {{$t('qrcode_add_default_logo')}}
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-stepper-content>
              <v-stepper-content :step="hasPrefixOptions ? 3 : 2">
                <v-row>
                  <v-col>
                    <div class="mb-8">
                      <div class="file-format-label font-weight-bold pb-1">{{$t('choose_file_format')}}:</div>
                      <span>{{$t('choose_file_format_hint')}}</span>
                      <div class="mt-2">
                        <span @click="selectFormat(format)" class="pill font-weight-bold mr-2" :class="{ 'selected': format === form.format }" :key="format" v-for="format in filteredFormats">
                          {{format}}
                        </span>
                      </div>
                    </div>

                    <div class="mb-6">
                      <div class="file-format-label font-weight-bold pb-1">{{$t('naming_convention')}}:</div>
                      <v-text-field ref="filename" dense v-model="form.filename">
                        <template v-slot:append>
                          <span>
                            .{{form.format}}
                          </span>
                        </template>
                      </v-text-field>
                      <div class="hint">
                        {{$t('naming_convention_hint')}}
                      </div>
                      <div>
                        <span @click="toggleGeneralField(field)" :class="{ 'selected': isGeneralFieldSelected(field)}" class="pill font-weight-bold mr-2 mb-2" :key="`general-field-${field}`" v-for="field in generalFields">{{field}}</span>
                      </div>
                      <div class="hint">{{$t('tags')}}:</div>
                      <div>
                        <span class="pill font-weight-bold mr-2 mb-2" @click="toggleTag(field)" :class="{ 'selected': isTagSelected(field)}" :key="field.key" v-for="field in activeScmFields">{{ field.key === 'scantrust_upload_batch' ? $t('upload_batch') : field.key }}</span>
                      </div>
                    </div>

                    <!-- <div v-if="mode != 'single'">
                      <v-btn @click="download" color="primary" :loading="loading" :disabled="count > 1000 || codes.length > 1000">
                        <v-icon class="mr-2">mdi-download</v-icon>
                        <span>Download ZIP</span>
                      </v-btn>
                      <div class="error-ctn font-weight-bold" v-if="count > 1000 || codes.length > 1000">
                        Too many codes selected! You can only download up to 1000 codes at a time.
                      </div>
                      <div class="disclaimer pt-2">
                        <div>
                          The zip file contains all selected QR codes in the chosen file format with the above naming convention.<br>
                        </div>
                      </div>
                    </div> -->
                  </v-col>
                </v-row>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
        <v-card-actions>
          <v-row v-if="step === 1" class="dialog-footer pa-3">
            <div class="mr-auto"></div>
            <v-btn class="mr-2" text @click="close">{{$t('cancel')}}</v-btn>
            <v-btn class="mr-2" @click="handleFirstStepNext">{{$t('next')}}</v-btn>
          </v-row>
          <v-row v-if="hasPrefixOptions && step === 2" class="dialog-footer pa-3">
            <div class="mr-auto"></div>
            <v-btn class="mr-2" text @click="step = 1">{{$t('back')}}</v-btn>
            <v-btn class="mr-2" @click="step = 3">{{$t('next')}}</v-btn>
          </v-row>
          <v-row  v-if="hasPrefixOptions && step === 3 || !hasPrefixOptions && step === 2" class="dialog-footer pa-3 align-center">
            <div>
              <v-checkbox v-if="templateToUpdate || templateToCreate" class="ml-2" v-model="createTemplateToggle" hide-details dense :label="$t('save_as_qr_template')"></v-checkbox>
              <!-- <v-checkbox v-if="templateToCreate" class="ml-2" v-model="createTemplateToggle" hide-details dense :label="$t('save_as_template')"></v-checkbox> -->
              <v-text-field v-if="(templateToUpdate || templateToCreate) && createTemplateToggle === true" v-model="createName" :maxlength="200" :placeholder="$t('qr_template_name')" class="mt-2 ml-3" dense outlined></v-text-field>
            </div>
            <div class="mr-auto"></div>
            <v-btn class="mr-2" text @click="hasPrefixOptions ? step = 2 : step = 1">{{$t('back')}}</v-btn>
            <v-btn class="mr-2" @click="download" :loading="loading" :disabled="count > 1000 || codes.length > 1000">{{$t('download')}}</v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import QRService from '@/services/qr'
import amplitude from 'amplitude-js'
import DOMPurify from 'dompurify'
import Api from '@/api'
import ImageCropper from '@/components/utils/ImageCropper'
import ColorInput from '@/components/utils/ColorInput'
import TextAroundQrCodeSettings from '@/components/TextAroundQrCodeSettings'
import Utils from '@/utils/utils'
import { analyzeQrSvg } from '@/utils/analyzeQrSvg'

export default {
  name: 'Download',
  components: {
    ImageCropper,
    ColorInput,
    TextAroundQrCodeSettings
  },
  // single / product / bulk
  props: ['mode', 'codes', 'selectAll', 'count', 'campaign', 'product', 'isDisabled', 'elabelData', 'enableQrTextSettings', 'hideActivator'],
  data () {
    return {
      elabelIcon: {
        svg: 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI3LjYuMSwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IuWbvuWxgl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIKCSB2aWV3Qm94PSIwIDAgMTMwIDEzMCIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTMwIDEzMDsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsOiNGRkZGRkY7fQoJLnN0MXtmaWxsLXJ1bGU6ZXZlbm9kZDtjbGlwLXJ1bGU6ZXZlbm9kZDt9Cjwvc3R5bGU+CjxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik0zLjIsMGgxMjMuNWMxLjgsMCwzLjIsMS40LDMuMiwzLjJ2MTIzLjVjMCwxLjgtMS40LDMuMi0zLjIsMy4ySDMuMmMtMS44LDAtMy4yLTEuNC0zLjItMy4yVjMuMgoJQzAsMS40LDEuNCwwLDMuMiwweiIvPgo8Zz4KCTxwb2x5Z29uIHBvaW50cz0iNzEuOCw5My41IDcxLjgsNDkuMiA1OC4yLDQ5LjIgNDQuNiw0OS4yIDQ0LjYsNjIuOCA1OC4yLDYyLjggNTguMiw5My41IDQ0LjcsOTMuNSA0NC43LDEwNy4xIDU4LjIsMTA3LjEgCgkJNTguMywxMDcuMSA3MS44LDEwNy4xIDcxLjgsMTA3LjEgODUuNCwxMDcuMSA4NS40LDkzLjUgCSIvPgoJPHBhdGggZD0iTTc0LjEsMzJjMCw1LTQuMSw5LjEtOS4xLDkuMVM1NiwzNyw1NiwzMnM0LjEtOS4xLDkuMS05LjFDNzAsMjIuOSw3NC4xLDI3LDc0LjEsMzJ6Ii8+CjwvZz4KPHBhdGggY2xhc3M9InN0MSIgZD0iTTExMCw4LjhIMjBjLTYuMiwwLTExLjMsNS0xMS4zLDExLjJ2OTBjMCw2LjIsNSwxMS4yLDExLjIsMTEuMmg5MGM2LjIsMCwxMS4yLTUsMTEuMi0xMS4yVjIwCglDMTIxLjMsMTMuOCwxMTYuMiw4LjgsMTEwLDguOHogTTExNy41LDExMGMwLDQuMS0zLjQsNy41LTcuNSw3LjVIMjBjLTQuMSwwLTcuNS0zLjQtNy41LTcuNVYyMGMwLTQuMSwzLjQtNy41LDcuNS03LjVoOTAKCWM0LjEsMCw3LjUsMy40LDcuNSw3LjVWMTEweiIvPgo8L3N2Zz4K',
        png: 'data:image/png;base64,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'
      },
      selectedTemplate: null,
      templateToUpdate: false,
      templateToCreate: true,
      templateToDelete: null,
      templateToRename: null,
      createTemplateToggle: false,
      deletingTemplate: false,
      renameTemplateDialog: false,
      renamingTemplate: false,
      renameName: '',
      createName: '',
      deleteQrDialog: false,
      hasContrastIssue: false,
      step: 1,
      dialog: false,
      generalFields: ['sku', 'product_name', 'code_id', 'brand_name'],
      loading: false,
      form: {
        qr_scale: 2,
        text_scale: 2,
        format: 'svg',
        filename: '{code_id}',
        background: '#FFFFFF',
        color: '#000000',
        image: null
      },
      textSettings: {
        text: {},
        selectedTexts: {},
        iconSetting: 'none'
      },
      textPositions: [
        { key: 'top', description: this.$t('top') },
        { key: 'right', description: this.$t('right') },
        { key: 'bottom', description: this.$t('bottom') },
        { key: 'left', description: this.$t('left') }
      ],
      hasTextOverflow: false,
      textOverflowDetails: [],
      showTextDownscaledWarning: false,
      qrBoundingBox: null,
      hasStoredPrefix: false,
      selectedPrefix: '',
      showPrefixRequiredMessage: false,
      showPrefixConflictMessage: false,
      batchSize: 100
    }
  },
  watch: {
    selectedTemplate (template) {
      if (template) {
        this.resetWarnings()
        this.resetForm()
        this.createTemplateToggle = false
        const overrides = {}
        for (const key of Object.keys(this.form)) {
          if (key in template) {
            overrides[key] = template[key]
          }
        }
        this.form = { ...this.form, ...overrides }
        const qrToTextScaleMap = {
          1: 2,
          2: 2,
          3: 2,
          4: 3,
          5: 3,
          6: 3,
          7: 3,
          8: 6,
          9: 6,
          10: 6
        }

        this.form.qr_scale = template.scale < 10 ? template.scale : 10
        const isV2Template = template.dpi === 0
        if (isV2Template && template.text.scale) {
          this.form.text_scale = template.text.scale
        } else {
          if (qrToTextScaleMap[this.form.qr_scale]) {
            this.form.text_scale = qrToTextScaleMap[this.form.qr_scale]
          } else {
            this.form.text_scale = 6
            this.showTextDownscaledWarning = true
          }
        }

        if (this.form.filename) {
          this.form.filename = this.form.filename.split(`.${this.form.format}`)[0]
        }
        if (this.form.image && this.elabelIcon.svg.indexOf(this.form.image) >= 0) {
          this.form.image = `data:image/svg+xml;base64,${this.form.image}`
        }

        this.textSettings.iconSetting = this.selectedTemplate.image ? 'inside' : Object.values(template.text).some(value => typeof value === 'string' && value.includes('ⓘ ')) ? 'outside' : 'none'
      }
    },

    form: {
      deep: true,
      handler (newVal) {
        this.checkContrast()
        if (this.selectedTemplate) {
          const formTemp = JSON.parse(JSON.stringify(newVal))
          // Hack to have both image without prefix for comparison
          if (formTemp.image.indexOf(this.elabelIcon.svg) > -1 && formTemp.image.indexOf(',') > -1) {
            formTemp.image = formTemp.image.split(',')[1]
          }
          delete formTemp.name

          const selectedTemplateTemp = JSON.parse(JSON.stringify(this.selectedTemplate))
          if (selectedTemplateTemp.text) {
            selectedTemplateTemp.text_scale = selectedTemplateTemp.text.scale
            delete selectedTemplateTemp.text
          }
          selectedTemplateTemp.qr_scale = selectedTemplateTemp.scale
          delete selectedTemplateTemp.scale
          delete selectedTemplateTemp.quiet_zone
          delete selectedTemplateTemp.ec_level
          delete selectedTemplateTemp.dpi
          delete selectedTemplateTemp.id
          delete selectedTemplateTemp.name
          delete selectedTemplateTemp.version

          // console.log('formTemp:', formTemp)
          // console.log('selectedTemplateTemp:', selectedTemplateTemp)
          if (!Utils.deepCompare(formTemp, selectedTemplateTemp)) {
            this.templateToUpdate = true
          } else {
            this.templateToUpdate = false
            this.templateToCreate = false
          }
        } else {
          this.templateToUpdate = false
          this.templateToCreate = true
        }
      }
    },
    'form.format' (newVal) {
      if (this.form.format === 'svg' && this.isAvailable('qrmanager:elabel') && (this.form.image === this.elabelIcon.png || this.form.image === this.elabelIcon.svg)) {
        this.form.image = this.elabelIcon.svg
      } else if (this.isAvailable('qrmanager:elabel') && (this.form.image === this.elabelIcon.png || this.form.image === this.elabelIcon.svg)) {
        this.form.image = this.elabelIcon.png
      }
    },
    textSettings: {
      deep: true,
      handler (newVal) {
        this.checkContrast()
        if (this.selectedTemplate) {
          const textSettingsCopy = JSON.parse(JSON.stringify(newVal))
          const { text, iconSetting } = textSettingsCopy
          const { scale, ...rest } = JSON.parse(JSON.stringify(this.selectedTemplate.text))
          const selectedTemplateTextCopy = rest
          // Remove empty fields from selectedTemplate.text to ensure proper comparison
          Object.keys(selectedTemplateTextCopy).forEach(key => {
            if (!selectedTemplateTextCopy[key]) {
              delete selectedTemplateTextCopy[key]
            }
          })

          // Revert variable replacement changes
          this.textPositions.forEach(position => {
            const key = position.key
            if (this.textSettings.selectedTexts[key]) {
              text[key] = this.textSettings.selectedTexts[key].text
            }
          })

          const selectedTemplateIconSetting = this.selectedTemplate.image ? 'inside' : Object.values(selectedTemplateTextCopy).some(value => typeof value === 'string' && value.includes('ⓘ ')) ? 'outside' : 'none'

          if ((this.codes.length === 1 && !Utils.deepCompare(text, selectedTemplateTextCopy)) || iconSetting !== selectedTemplateIconSetting) {
            this.templateToUpdate = true
          } else {
            this.templateToUpdate = false
            this.templateToCreate = false
          }
        } else {
          this.templateToUpdate = false
          this.templateToCreate = true
        }
      }
    },
    previewUrl: {
      immediate: true,
      handler () {
        this.checkSvgAndOverflow()
      }
    },
    dialog (isOpen) {
      // previewUrl does not change when the dialog opens (codes are already set), so trigger
      // the SVG analysis explicitly here. checkSvgAndOverflow is guarded to no-op while closed.
      if (isOpen) {
        this.checkSvgAndOverflow()
      }
    },
    createTemplateToggle () {
      this.createName = ''
    }
  },
  computed: {
    ...mapGetters(['activeScmFields', 'prefixes', 'systemPrefix', 'qrTemplates', 'isAvailable']),

    qrScale () {
      return this.form.qr_scale
    },

    textScale () {
      return this.form.text_scale
    },

    filteredFormats () {
      const formats = ['svg', 'pdf', 'eps', 'jpg', 'png', 'tiff']

      for (const key in this.textSettings.text) {
        if (this.textSettings.text[key].indexOf('ⓘ') > -1) {
          return formats.filter((format) => format !== 'eps')
        }
      }
      return formats
    },

    prefix () {
      if (this.hasPrefixOptions) {
        return this.selectedPrefix
      }
      if (this.nonSystemPrefixes.length === 1) {
        return this.nonSystemPrefixes[0].prefix
      }
      return this.systemPrefix
    },

    formattedPrefixes () {
      return this.prefixes.filter((prefix) => prefix.type !== 'system').map(item => {
        let displayText = item.prefix
        if (item.type === 'hosted-domain') {
          displayText += ` (${this.$t('prefix_custom_domain')})`
        } else if (item.type === 'registered') {
          displayText += ` (${this.$t('prefix_self_hosted')})`
        }
        return { ...item, displayText }
      })
    },

    previewUrl () {
      if (this.codes && this.codes[0] && this.codes[0].message) {
        const gs1DataAttributes = this.codes[0].scm_data.gs1_data_attributes ? encodeURIComponent(`?${this.codes[0].scm_data.gs1_data_attributes}`) : ''

        const texts = Object.entries(this.textSettings.text).filter(([key]) => key !== 'color')

        if (texts.length) {
          // split energy text and add blank space to left and right sides to prevent energy text from being cut off
          if (this.form.text_scale === 2) {
            if (this.form.qr_scale === 1 || this.form.qr_scale === 2) {
              texts.forEach((text) => {
                if (text[1].startsWith('E(100ml)=')) {
                  text[1] = 'E(100ml)=' + '\n' + text[1].slice('E(100ml)='.length)
                }
              })
            }
            if (this.form.qr_scale === 1) {
              const keys = texts.map((text) => text[0])
              if (!keys.includes('right')) texts.push(['right', ' '])
              if (!keys.includes('left')) texts.push(['left', ' '])
            }
          }

          let textQueryString = ''
          texts.forEach(([key, value]) => {
            textQueryString += `&text_${key}=${encodeURIComponent(value)}`
          })

          if (this.textSettings.text?.color) {
            textQueryString += `&text_color=${this.textSettings.text.color.split('#')[1]}`
          }

          return `${Api.qr_service_url}v2/single/?ec_level=q&output_format=svg&color=%23${this.form.color.split('#')[1]}&background=%23${this.form.background.split('#')[1]}&quiet_zone=4&qr_scale=${this.form.qr_scale}&text_scale=${this.form.text_scale}&content=${this.prefix}${this.codes[0].message}${gs1DataAttributes}${textQueryString}`
        } else {
          return `${Api.qr_service_url}v2/single/?ec_level=q&output_format=svg&color=%23${this.form.color.split('#')[1]}&background=%23${this.form.background.split('#')[1]}&quiet_zone=4&qr_scale=${this.form.qr_scale}&text_scale=${this.form.text_scale}&content=${this.prefix}${this.codes[0].message}${gs1DataAttributes}`
        }
      }
      return ''
    },

    logoStyle () {
      // if we detected the QR square, center the icon inside it
      const iconScale = 0.28
      if (this.qrBoundingBox) {
        return {
          position: 'absolute',
          left: `${this.qrBoundingBox.cx * 100}%`,
          top: `${this.qrBoundingBox.cy * 100}%`,
          width: `${this.qrBoundingBox.w * iconScale * 100}%`,
          height: 'auto',
          transform: 'translate(-50%, -50%)',
          backgroundColor: this.form.background || 'white'
        }
      }
      // fallback
      return {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: `${iconScale * 100}%`,
        height: 'auto',
        transform: 'translate(-50%, -50%)',
        backgroundColor: this.form.background || 'white'
      }
    },

    image () {
      return this.textSettings.iconSetting !== 'inside' ? null : this.form.image && this.form.image.indexOf('data:image/svg+xml;base64,') === 0 ? this.form.image.split('data:image/svg+xml;base64,')[1] : this.form.image
    },

    nonSystemPrefixes () {
      return this.prefixes.filter((prefix) => prefix.type !== 'system')
    },

    hasPrefixOptions () {
      return this.nonSystemPrefixes.length > 1
    },

    haveConflictingPrefixes () {
      const uniquePrefixes = new Set(
        this.codes.map((code) => code.scm_data.scantrust_url_prefix || this.systemPrefix)
      )
      return uniquePrefixes.size > 1
    },

    hasObsoletePrefix () {
      return this.codes.some(code => {
        const storedPrefix = code.scm_data.scantrust_url_prefix
        return storedPrefix && !this.prefixes.some((prefix) => prefix.prefix === storedPrefix)
      })
    },

    isChangingPrefix () {
      if (this.codes.length > 1) {
        return this.codes.some(code => {
          const storedPrefix = code.scm_data.scantrust_url_prefix
          return !storedPrefix || storedPrefix !== this.prefix
        })
      }
      const storedPrefix = this.codes[0].scm_data.scantrust_url_prefix
      return (storedPrefix && storedPrefix !== this.prefix) || (!storedPrefix && this.prefix !== this.systemPrefix)
    }

  },
  methods: {
    onTextSettingsUpdated (updatedData) {
      this.$set(this, 'textSettings', updatedData)
    },

    onQrScaleUpdated (sliderIndex) {
      if (sliderIndex === 0) {
        this.form.qr_scale = 1
        this.form.text_scale = 2
      } else if (sliderIndex === 1) {
        this.form.qr_scale = 2
        this.form.text_scale = 2
      } else if (sliderIndex === 2) {
        this.form.qr_scale = 4
        this.form.text_scale = 3
      } else if (sliderIndex === 3) {
        this.form.qr_scale = 6
        this.form.text_scale = 3
      } else if (sliderIndex === 4) {
        this.form.qr_scale = 10
        this.form.text_scale = 6
      }
    },

    onTextScaleUpdated (size) {
      if (size === 'small') {
        this.form.text_scale = 2
      } else if (size === 'medium') {
        this.form.text_scale = 3
      } else if (size === 'large') {
        this.form.text_scale = 6
      }
    },

    async checkSvgAndOverflow () {
      // Only analyze the QR SVG while the download dialog is open and showing the preview.
      // When the dialog is closed (e.g. the per-code <download> instances rendered in the
      // product codes list), skip the network fetch entirely to avoid one qr-svc call per code.
      if (!this.dialog || !this.previewUrl) {
        this.hasTextOverflow = false
        this.textOverflowDetails = []
        this.qrBoundingBox = null
        return
      }

      try {
        const res = await fetch(this.previewUrl)
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`)
        const svgText = await res.text()

        // Analyze the SVG offscreen:
        // - texts: determine if each line overflows
        // - qrBoundingBox: normalized QR square { x,y,w,h,cx,cy } in 0..1 of the SVG viewport
        const { texts, qrBoundingBox } = await analyzeQrSvg(svgText)

        this.textOverflowDetails = texts
        this.hasTextOverflow = texts.some(text => text.overflow)
        this.qrBoundingBox = qrBoundingBox
      } catch (err) {
        console.error('analyze svg failed:', err)
        this.hasTextOverflow = false
        this.textOverflowDetails = []
        this.qrBoundingBox = null
      }
    },

    isBase64StringSVG (base64String) {
      // Decode the base64 string to binary data
      const decodedData = atob(base64String)

      // Check if the decoded data starts with an SVG XML declaration and contains an <svg> element
      return decodedData.startsWith('<?xml version=') && decodedData.includes('<svg')
    },

    toDataUrl (base64) {
      if (base64) {
        if (base64.indexOf('data:image') === 0) {
          return base64
        } else if (!this.isBase64StringSVG(base64)) {
          return `data:image/png;base64,${base64}`
        } else {
          return `data:image/svg+xml;base64,${base64}`
        }
      }
    },

    addDefaultLogo () {
      this.$refs['text-around-qr-code-settings'].updateIconSetting('inside')
      if (this.form.format === 'svg') {
        this.form.image = this.elabelIcon.svg
      } else {
        this.form.image = this.elabelIcon.png
      }
    },

    hexToRgb (hex) {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
      hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b
      })

      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null
    },

    calcLuminance (r, g, b) {
      var a = [r, g, b].map((v) => {
        v /= 255
        return v <= 0.03928
          ? v / 12.92
          : Math.pow((v + 0.055) / 1.055, 2.4)
      })
      return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
    },

    checkContrast () {
      const bgRbg = this.hexToRgb(this.form.background)
      const qrRbg = this.hexToRgb(this.form.color)
      if (!bgRbg || !qrRbg) {
        this.hasContrastIssue = false
        return
      }
      const backgroundLuminance = this.calcLuminance(bgRbg.r, bgRbg.g, bgRbg.b)
      const qrLuminance = this.calcLuminance(qrRbg.r, qrRbg.g, qrRbg.b)

      const ratio = backgroundLuminance > qrLuminance
        ? ((backgroundLuminance + 0.05) / (qrLuminance + 0.05))
        : ((qrLuminance + 0.05) / (backgroundLuminance + 0.05))

      this.hasContrastIssue = ratio < 3.8
    },

    deleteQrTemplateConfirm (template) {
      this.templateToDelete = template
      this.deleteQrDialog = true
    },

    async deleteQrTemplate () {
      this.deletingTemplate = true
      await this.$store.dispatch('deleteQrTemplate', { id: this.templateToDelete.id })
      this.deletingTemplate = false
      this.templateToDelete = null
      this.deleteQrDialog = false
    },

    openRenameTemplateDialog (template) {
      this.templateToRename = template
      this.renameName = template.name
      this.renameTemplateDialog = true
    },

    closeRenameTemplateDialog () {
      this.templateToRename = null
      this.renameName = ''
      this.renameTemplateDialog = false
    },

    async renameTemplate () {
      this.renamingTemplate = true
      await this.$store.dispatch('updateQrTemplate', { id: this.templateToRename.id, template: { name: this.renameName } })
      this.renamingTemplate = false
      this.closeRenameTemplateDialog()
    },

    getTemplatePreviewUrl (template) {
      const gs1DataAttributes = this.codes[0]?.scm_data.gs1_data_attributes ? encodeURIComponent(`?${this.codes[0].scm_data.gs1_data_attributes}`) : ''

      return `${Api.qr_service_url}v2/single/?color=%23${template.color.split('#')[1]}&background=%23${template.background.split('#')[1]}&quiet_zone=4&qr_scale=${template.scale}&text_scale=${template.text.scale}&content=${this.prefix}${this.codes[0]?.message}${gs1DataAttributes}`
    },

    initSelectedPrefix () {
      if (this.hasObsoletePrefix || (this.codes.length > 1 && this.haveConflictingPrefixes)) return

      const storedPrefix = this.codes[0].scm_data.scantrust_url_prefix
      if (storedPrefix && this.nonSystemPrefixes.length > 0 && this.nonSystemPrefixes.some((prefix) => prefix.prefix === storedPrefix)) {
        this.selectedPrefix = storedPrefix
        this.hasStoredPrefix = true
      }
    },

    onPrefixChange () {
      this.hasStoredPrefix = false
      this.showPrefixRequiredMessage = false
      if (this.mode === 'product' && this.haveConflictingPrefixes) {
        this.showPrefixConflictMessage = true
      }
    },

    handleFirstStepNext () {
      if (this.hasPrefixOptions && !this.selectedPrefix) {
        this.showPrefixRequiredMessage = true
      } else {
        this.step = 2
      }
    },

    close () {
      this.step = 1
      this.dialog = false
      this.selectedTemplate = null
      this.selectedPrefix = ''
      this.hasStoredPrefix = false
      this.showPrefixRequiredMessage = false
      this.showPrefixConflictMessage = false
      this.templateToCreate = false
      this.templateToUpdate = false
      this.createTemplateToggle = false
      this.textSettings = {
        text: {
          color: '#000000'
        },
        selectedTexts: {},
        iconSetting: null
      }
      this.resetWarnings()
    },

    deleteLogo () {
      this.$refs['text-around-qr-code-settings'].updateIconSetting('none')
      this.form.image = ''
    },

    onImageSelect (image) {
      this.$refs['text-around-qr-code-settings'].updateIconSetting('inside')
      this.form = Object.assign({}, this.form, { image: image })
    },

    toggleTag (tag) {
      if (this.isTagSelected(tag)) {
        this.form.filename = this.form.filename.replace(`{${tag.key}}`, '')
      } else {
        this.form.filename += `{${tag.key}}`
      }
      this.$refs.filename.focus()
    },

    toggleGeneralField (field) {
      if (this.isGeneralFieldSelected(field)) {
        this.form.filename = this.form.filename.replace(`{${field}}`, '')
      } else {
        this.form.filename += `{${field}}`
      }
      this.$refs.filename.focus()
    },

    isGeneralFieldSelected (field) {
      return ~this.form.filename.indexOf(`{${field}}`)
    },

    isTagSelected (tag) {
      return ~this.form.filename.indexOf(`{${tag.key}}`)
    },

    resetForm () {
      this.form = {
        qr_scale: 2,
        text_scale: 2,
        format: 'svg',
        name: '',
        filename: '{code_id}',
        background: '#FFFFFF',
        color: '#000000',
        image: null
      }
    },

    resetWarnings () {
      this.showTextDownscaledWarning = false
      this.hasTextOverflow = false
    },

    open () {
      if (this.mode === 'single') {
        amplitude.getInstance().logEvent('click preview & download (single)')
      } else if (this.mode === 'codes') {
        amplitude.getInstance().logEvent('click download')
      } else {
        amplitude.getInstance().logEvent('click preview & download (bulk)')
      }
      this.initSelectedPrefix()
      this.dialog = true
      this.resetForm()

      // Load templates lazily on open. Doing this in created() fired one GET /qr-templates/
      // per <download> instance — and the list renders one instance per code row.
      if (!this.qrTemplates.length) {
        this.$store.dispatch('loadQrTemplates')
      }

      // if (this.isAvailable('qrmanager:elabel')) {
      //   this.form.image = this.elabelIcon.png
      // }
    },

    selectFormat (format) {
      this.form.format = format
    },

    isTag (string) {
      return this.activeScmFields.find((field) => {
        return field.key === string
      })
    },

    sanitizeItemFileName (filename) {
      return DOMPurify.sanitize(filename)
    },
    sanitizeItemColor (color) {
      return DOMPurify.sanitize(color)
    },

    generateFileName (code, string) {
      const matches = string.match(/\{(.*?)\}/)

      if (matches) {
        const submatch = matches[1]
        const before = string.substr(0, string.indexOf('{'))
        const after = string.substr(string.indexOf('}') + 1)
        if (this.isTag(submatch)) {
          return this.generateFileName(code, `${before}${code.scm_data[submatch] ? code.scm_data[submatch] : ''}${after}`)
        } else {
          if (submatch === 'code_id') {
            return this.generateFileName(code, `${before}${code.message}${code.scm_data.gs1_data_attributes ? `-${code.scm_data.gs1_data_attributes}` : ''}${after}`)
          } else if (submatch === 'product_name') {
            return this.generateFileName(code, `${before}${code.product ? code.product.name : ''}${after}`)
          } else if (submatch === 'sku') {
            return this.generateFileName(code, `${before}${code.product ? code.product.sku : ''}${after}`)
          } else if (submatch === 'brand_name') {
            return this.generateFileName(code, `${before}${code.brand ? code.brand.name : ''}${after}`)
          } else {
            return this.generateFileName(code, `${before}${submatch}${after}`)
          }
        }
      } else {
        return string
      }
    },

    async updatePrefixesForAllCodes (allCodes, index, isRetry) {
      let codesCopy = JSON.parse(JSON.stringify(allCodes))
      codesCopy = codesCopy.splice(index, this.batchSize)

      if (!codesCopy.length) return

      return new Promise((resolve, reject) => {
        this.$store.dispatch('updateSelectedPrefixes', {
          codes: codesCopy,
          prefix: this.prefix,
          reference: 'QR manager: updating scantrust prefix'
        }).then(async (res) => {
          resolve(this.updatePrefixesForAllCodes(allCodes, index + this.batchSize))
        }).catch((err) => {
          if (err && err.response && err.response.status === 429) {
            console.log('Throttling prefix update')
            setTimeout(() => {
              resolve(this.updatePrefixesForAllCodes(allCodes, index, true))
            }, 10000)
          }
        })
      })
    },

    createOrUpdateTemplate () {
      // Save dynamic variables key instead of actual values
      const templateTexts = {}
      Object.entries(this.textSettings.selectedTexts).forEach(([key, value]) => {
        templateTexts[key] = value.text
      })
      if (this.enableQrTextSettings) {
        // Check if template is already selected
        if (this.templateToUpdate) {
          console.log('update template because template selected but modified')
          this.$store.dispatch('createQrTemplate', {
            ...this.form,
            name: this.createName,
            image: this.image,
            text: {
              ...templateTexts,
              color: this.textSettings.text.color,
              scale: this.form.text_scale
            }
          })
          // this.$store.dispatch('updateQrTemplate', { id: this.selectedTemplate.id, template: this.form })
        } else if (this.templateToCreate) {
          this.$store.dispatch('createQrTemplate', {
            ...this.form,
            name: this.createName,
            image: this.image,
            text: {
              ...templateTexts,
              color: this.textSettings.text.color,
              scale: this.form.text_scale
            }
          })
        }
      } else {
        // Check if template is already selected
        if (this.templateToUpdate) {
          console.log('update template because template selected but modified')
          this.$store.dispatch('createQrTemplate', this.form)
          // this.$store.dispatch('updateQrTemplate', { id: this.selectedTemplate.id, template: this.form })
        } else if (this.templateToCreate) {
          this.$store.dispatch('createQrTemplate', this.form)
        }
      }
    },

    async download () {
      if (!this.prefix) {
        this.$store.dispatch('showSnackBar', { message: this.$t('prefix_missing') })
        return
      }

      amplitude.getInstance().logEvent('download code .zip file')
      this.loading = true
      const invalidFilenameChars = /[/\\:*?"<>|]/g
      const replacementFilenameChar = '-'
      const codesArray = []
      // split energy text and add blank space to left and right sides to prevent energy text from being cut off
      const textObj = { ...this.textSettings.text }
      if (Object.keys(textObj).length && this.form.text_scale === 2) {
        if (this.form.qr_scale === 1 || this.form.qr_scale === 2) {
          for (const key of Object.keys(textObj)) {
            if (key === 'color') continue
            if (textObj[key].startsWith('E(100ml)=')) {
              textObj[key] = 'E(100ml)=' + '\n' + textObj[key].slice('E(100ml)='.length)
            }
          }
        }
        if (this.form.qr_scale === 1) {
          if (textObj.right === undefined) textObj.right = ' '
          if (textObj.left === undefined) textObj.left = ' '
        }
      }

      if (this.selectAll) {
        try {
          const allCodes = await this.$store.dispatch('loadAllCodes', { count: this.count || 0, campaign: this.campaign, product: this.product })
          allCodes.forEach((code) => {
            const filename = this.generateFileName(code, this.form.filename).replace(invalidFilenameChars, replacementFilenameChar)
            const codeInfo = {
              filename: `${filename}.${this.form.format}`,
              content: `${this.prefix}${code.message}${code.scm_data.gs1_data_attributes ? `?${code.scm_data.gs1_data_attributes}` : ''}`,
              output_format: this.form.format,
              qr_scale: this.form.qr_scale,
              color: this.form.color,
              background: this.form.background,
              text: { ...textObj, scale: this.form.text_scale },
              name: this.createName,
              image: this.image
            }
            codesArray.push(codeInfo)
          })

          if (this.isChangingPrefix) {
            await this.updatePrefixesForAllCodes(allCodes, 0)
          }

          await QRService.generateQrZip({ codes: codesArray })

          if (this.createTemplateToggle) {
            this.createOrUpdateTemplate()
          }

          this.close()
        } catch (err) {
          this.$store.dispatch('showSnackBar', { message: err?.message || this.$t('download_error_msg') })
        } finally {
          this.loading = false
        }
      } else {
        try {
          if (this.isChangingPrefix) {
            await this.$store.dispatch('updateSelectedPrefixes', {
              codes: this.codes,
              prefix: this.prefix,
              reference: 'QR manager: updating scantrust prefix'
            })
          }
          this.codes.forEach((code) => {
            const filename = this.generateFileName(code, this.form.filename).replace(invalidFilenameChars, replacementFilenameChar)
            const codeInfo = {
              filename: `${filename}.${this.form.format}`,
              content: `${this.prefix}${code.message}${code.scm_data.gs1_data_attributes ? `?${code.scm_data.gs1_data_attributes}` : ''}`,
              output_format: this.form.format,
              qr_scale: this.form.qr_scale,
              color: this.form.color,
              background: this.form.background,
              text: { ...textObj, scale: this.form.text_scale },
              image: this.image
            }
            codesArray.push(codeInfo)
          })
          await QRService.generateQrZip({ codes: codesArray })
          if (this.createTemplateToggle) {
            this.createOrUpdateTemplate()
          }
          this.close()
        } catch (err) {
          this.$store.dispatch('showSnackBar', { message: err?.message || this.$t('download_error_msg') })
        } finally {
          this.loading = false
        }
      }
    }
  }
}

</script>

<style lang="sass" scoped>
.prefixes-ctn
  height: 220px
  .prefix
    max-width: 100%

.select-qr-preview
  position: relative
  .template-qr-preview
    width: 46px
  .template-qr-logo
    position: absolute
    width: 13px
    padding: 2px
    top: calc(23px - 13px / 2)
    left: calc(23px - 13px / 2)

.preview-item-title
  font-size: 15px

.preview-item-subtitle
  color: #0009
  font-size: 13px
  font-weight: 500
  line-height: 1.4
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.preview-col
  border-left: 1px solid rgba(0,0,0,0.3)

.qr-preview
  width: 250px
  height: 100%
  text-align: center
  img
    margin-left: auto
    margin-right: auto
  .qr-preview-ctn
    margin-left: auto
    margin-right: auto
    position: relative
    width: 195px
    .qr
      width: 195px
    .logo
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)
      padding: 2%

.qr-downscale-warning
  margin-top: -20px
  margin-bottom: 20px

.warn
  background: #FFDB4D
  border-radius: 4px

.scrollable-content
  max-height: 50vh
  overflow-y: auto
  padding-right: 16px
  padding-bottom: 16px

@media screen and (min-width: 847px)
  .max-w-295
    max-width: 295px

.custom-v-select::v-deep input
    display: none !important

.color-error
  color: red

.error-ctn
  font-size: 14px
  color: red

.disclaimer
  color: #999999
  font-family: 14px
  line-height: 1.2

.label
  text-transform: uppercase
  color: #999999
  font-size: 16px

.file-format-label
  font-size: 17px

.pill
  display: inline-block
  text-align: center
  color: #00aeef
  border: 2px solid #00aeef
  border-radius: 4px
  padding: 4px 6px
  cursor: pointer
  &.selected
    color: white
    background: #00aeef

.dialog-footer
  box-shadow: 0 -1px 4px 0 rgba(0, 0, 0, 0.25)
  .v-input--selection-controls
    margin-top: 0px
    padding-top: 0px
</style>
